import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { backendService } from "../services/backend.service";
import type { Nurse } from "../utils/models";
import { NURSES_QUERY_KEY_ROOT } from "./nursesQueryKeys";
import type { RosterLoadState } from "./useRosterLookup";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";
import { teamRosterQueryOptions } from "./useTeamRoster";

type RosterNurseOfUser = RosterLoadState & { nurse: Nurse | undefined };

// HospitalDataProvider refetches this same entry after every roster write and patches it after a time-off
// change, so a page mounting shortly after doesn't need its own request to be current.
const ROSTER_STALE_TIME_MS = 5 * 60 * 1000;

const nursesQueryOptions = (departmentId: string | undefined, activeOnDate: string | undefined) => ({
	queryKey: [NURSES_QUERY_KEY_ROOT, departmentId, activeOnDate],
	queryFn: async () => {
		const response = await backendService.getNurses(departmentId, activeOnDate);
		return response.data;
	},
});

/**
 * The department roster, shared by every reader. With the schedule cache on it's `/roster`, which the Team page reads
 * too, asked for when a screen first reads it rather than when the app opens; off, `/team/nurses`, which the provider
 * loads at start.
 */
export const departmentRosterQueryOptions = (departmentId: string, isCacheEnabled: boolean) => {
	if (!isCacheEnabled) return { ...nursesQueryOptions(departmentId, undefined), staleTime: ROSTER_STALE_TIME_MS };
	const teamRoster = teamRosterQueryOptions(departmentId);
	return {
		...teamRoster,
		// `/roster` leaves out time off, weekly preferences and wage history. With the cache on, every screen reads
		// time off from the schedule weeks and wages from the current wage, so none reads those off this list.
		queryFn: async () => (await teamRoster.queryFn()) as Nurse[],
	};
};

/** The department roster query, shared by every hook that reads the whole roster so they hit one cache entry. */
export const useDepartmentRosterQuery = (departmentId: string) => {
	const isCacheEnabled = useScheduleCacheEnabled();
	return useQuery<Nurse[]>(departmentRosterQueryOptions(departmentId, isCacheEnabled));
};

/** One nurse from the department roster, re-rendering only when that nurse changes — for rows that each show one person. */
export const useRosterNurse = (departmentId: string, nurseId: number): Nurse | undefined => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const select = useCallback((roster: Nurse[]) => roster.find((nurse) => nurse.id === nurseId), [nurseId]);
	return useQuery<Nurse[], Error, Nurse | undefined>({ ...departmentRosterQueryOptions(departmentId, isCacheEnabled), select }).data;
};

/**
 * A user's own nurse record from the department roster, for their own settings, with the roster's load state so a
 * screen can wait for it. `/roster` refuses anyone but a head nurse in admin view, so callers turn it off for everyone
 * else; turned off it reports neither loading nor failed.
 */
export const useRosterNurseOfUser = (departmentId: string, userId: string, enabled: boolean): RosterNurseOfUser => {
	const isCacheEnabled = useScheduleCacheEnabled();
	const select = useCallback((roster: Nurse[]) => roster.find((nurse) => nurse.userId === userId), [userId]);
	const { data, isLoading, isLoadingError } = useQuery<Nurse[], Error, Nurse | undefined>({
		...departmentRosterQueryOptions(departmentId, isCacheEnabled),
		enabled,
		select,
	});
	// A failed background refetch keeps the loaded roster, so only a roster that never arrived counts as failed.
	return { nurse: data, isLoading, isError: isLoadingError };
};

export const useNurses = (departmentId?: string, activeOnDate?: string) => {
	return useQuery<Nurse[]>({
		...nursesQueryOptions(departmentId, activeOnDate),
		enabled: false,
	});
};
